import {
  ContactSchema,
  ConversationThreadSchema,
  GuestSchema,
} from '../../definitions/schemas';
import { CollaboratorType } from '../../enums/CollaboratorType';
import UserStore from '../../stores/observables/UserStore';

export const getConversationAuthor = (
  contactGuest?: ContactSchema
): GuestSchema => {
  const currentUser = UserStore.get();

  if (contactGuest) {
    return { ...contactGuest, type: CollaboratorType.Contact };
  }

  return {
    id: '',
    email: currentUser?.email || '',
    firstName: currentUser?.firstName,
    lastName: currentUser?.lastName,
    photo: currentUser?.photo,
    type: CollaboratorType.User,
  };
};

/**
 * Converts a given flat conversation threads array into a nested list, where
 * the children threads are nested within its parent.
 */
export const generateConversations = (
  threads: ConversationThreadSchema[]
): ConversationThreadSchema[] => {
  const accumulator: { [key: string]: ConversationThreadSchema } = {};

  threads.forEach((thread) => {
    const id = `thread-${thread.id}`;
    const parentId = `thread-${thread.parentId}`;

    if (thread.parentId && accumulator[parentId]) {
      if (!accumulator[parentId].children) {
        accumulator[parentId].children = [];
      }

      accumulator[parentId].children?.push(thread);
      return;
    }

    if (!accumulator[id]) {
      accumulator[id] = thread;
    }
  });

  const result: ConversationThreadSchema[] = [];

  Object.keys(accumulator).forEach((k) => {
    const thread = accumulator[k];
    // sorting children in ascending order
    thread.children?.sort((a, b) => a.createdAt - b.createdAt);
    result.push(thread);
  });

  // sorting conversations in descending order
  return result.sort((a, b) => a.createdAt - b.createdAt);
};

export const deleteConversationThread = (
  conversations: ConversationThreadSchema[],
  id: string,
  parentId?: string
) => {
  const newConversations = parentId
    ? deleteChildren(conversations, id, parentId)
    : deleteParent(conversations, id);

  return [...newConversations];
};

export const isParentThreadDeletable = (thread: ConversationThreadSchema) => {
  if (thread.children) {
    return (
      !thread.children.some(
        (child) => !canUserDeleteConversationThread(child)
      ) && canUserDeleteConversationThread(thread)
    );
  }
  return true;
};

export const canUserDeleteConversationThread = (
  thread: ConversationThreadSchema
) => {
  const currentUser = UserStore.get();
  if (!currentUser?.id) {
    return false;
  }

  return thread.owner.id === currentUser.id;
};

// get the conversation length
export const getConversationSize = (
  conversations: ConversationThreadSchema[]
) => {
  let size = 0;
  conversations.forEach((c) => {
    const threads = c.children?.length || 0;
    size = size + threads + 1;
  });

  return size;
};

export const conversationExists = (
  conversations: ConversationThreadSchema[],
  roomMessageId: string
) =>
  conversations.some((conversation): boolean => {
    if (conversation.id === roomMessageId) {
      return true;
    }

    return !conversation.children
      ? false
      : conversationExists(conversation.children, roomMessageId);
  });

// get the conversation object from rmid param
export const getParentConversationThread = (
  rmid: string,
  conversations: ConversationThreadSchema[]
) => {
  let thread = null;
  conversations.forEach((c) => {
    if (c.id === rmid) {
      thread = c;
      return;
    }
    const childrenThread = c.children?.some(({ id }) => id === rmid);
    if (childrenThread) {
      thread = c;
    }
  });

  return thread;
};

const deleteChildren = (
  conversations: ConversationThreadSchema[],
  id: string,
  parentId: string | undefined
) =>
  conversations.map((conversation) =>
    !conversation.children?.length || parentId !== conversation.id
      ? conversation
      : {
          ...conversation,
          children: conversation.children.filter((child) => child.id !== id),
        }
  );

const deleteParent = (
  conversations: ConversationThreadSchema[],
  id: string
) => {
  const newConversation = conversations.filter(
    (conversation) => conversation.id !== id
  );
  return newConversation;
};
